import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { IconAiAgent } from '@components/icons/ai-agent';
import { IconDropside } from '@components/icons/dropside';
import { IconPlan } from '@components/icons/icon-plan';
import { IconJourneyLogo } from '@components/icons/journey-logo';
import { IconList } from '@components/icons/list';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconPortal } from '@components/icons/portal';
import { IconSearch } from '@components/icons/search';
import { IconSettings } from '@components/icons/settings';
import { IconTemplate } from '@components/icons/template';
import { IconTraveller } from '@components/icons/traveller';
import { Menu } from '@components/menu';
import { Nav } from '@components/nav';
import { ProgressBar } from '@components/progress-bar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { getVisibleWidth } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { daysUntil } from '@utils/date';
import { formatNumber } from '@utils/numbers';
import { isMac } from '@utils/operating-system';

import { config } from 'app-config';

import { useCurrentCompany, usePlan } from 'api/authentication/hooks';
import { useCompleteOnboardingStep } from 'api/company/hooks';
import { usePortals } from 'api/portals/hooks';
import { SupportMenu } from 'components/support-menu';
import { UserMenu } from 'components/user-menu';
import { routePaths } from 'ui/authenticated/navigation';
import { useFeature } from 'ui/hooks';

import logotypeSvg from './50logo-Journeys-logotype.svg';
import { CompanySelector } from './company-selector';
import { Favorites } from './favorites';
import { Workspaces } from './workspaces';

import { useCommandPalette } from '../../../../components/command-palette';

const ICON_SIZE = 16;

export const MainNavbar = () => {
  const { t } = useScopedTranslate(locale);
  const { isEnabled: hasAIAgentsEnabled } = useFeature('FEATURE_AI_AGENTS');
  const company = useCurrentCompany();
  const { data: portals } = usePortals();
  const completeOnboardingStep = useCompleteOnboardingStep();
  const showCommandPalette = useCommandPalette();
  const navigate = useNavigate();

  const portalDomain = company?.portalDomain
    ? `https://${company.portalDomain}`
    : `${config.PORTAL_URL}`;

  return (
    <Nav
      stateStorageKey="main-nav-state"
      initialFixed={getVisibleWidth() > 1600}
      fixedHeader={
        <Nav.Header
          logo={({ isOpen }) =>
            isOpen ? <img src={logotypeSvg} alt="Journeys Logo" /> : <IconJourneyLogo />
          }
        >
          <UserMenu />
          <Nav.Button
            onClick={() => showCommandPalette()}
            label={t('core.search')}
            iconRight={<KeyboardShortcutText>{isMac ? '⌘ K' : 'Ctrl K'}</KeyboardShortcutText>}
            iconLeft={<IconSearch size={ICON_SIZE} />}
          />
          <CompanySelector />
        </Nav.Header>
      }
      fixedFooter={<PlanInformation />}
    >
      <Nav.Group>
        <Nav.Link
          href="travellers"
          label={t('travellers')}
          iconLeft={<IconTraveller size={ICON_SIZE} />}
        />
        <Nav.Link
          href={routePaths.companyLists}
          label={t('lists')}
          iconLeft={<IconList size={ICON_SIZE} />}
        />
        {hasAIAgentsEnabled && (
          <Nav.Link
            href={routePaths.aiAgents}
            label={t('aiAgents')}
            iconLeft={<IconAiAgent size={ICON_SIZE} />}
          />
        )}
        <Nav.Link
          href={routePaths.settingsGeneral}
          label={t('settings')}
          iconLeft={<IconSettings size={ICON_SIZE} />}
        />
        <Nav.ExternalLink
          href="https://www.50skills.com/templates"
          label={t('templates')}
          iconLeft={<IconTemplate size={ICON_SIZE} />}
          onClick={() => completeOnboardingStep('exploreTemplates')}
        />
        <Nav.Menu
          icon={<IconPortal size={ICON_SIZE} />}
          label={t('portal')}
          iconRight={<IconDropside />}
        >
          {portals?.map((portal) => (
            <Nav.MenuItemLink
              key={portal.shortId}
              iconRight={<IconOpenNewTab color="buttonIconPositive" size={ICON_SIZE} />}
              label={portal.name}
              to={portal?.url ?? `${portalDomain}/${portal.slugPath}/`}
              isExternal
              onSelect={() => completeOnboardingStep('explorePortal')}
            />
          ))}
          <Menu.Separator />
          <Nav.MenuItem
            label={t('portalManagement')}
            icon={<IconPortal color="elementPurple80" />}
            onSelect={() => navigate(routePaths.portals)}
          />
        </Nav.Menu>
      </Nav.Group>
      <SupportMenu />
      <Nav.Separator />
      <Favorites />
      <Workspaces />
    </Nav>
  );
};

const PlanInformation = () => {
  const { t } = useScopedTranslate(locale);
  const plan = usePlan();

  const daysUntilExpiration = plan?.dateToClose ? daysUntil(new Date(plan.dateToClose)) : Infinity;
  const creditsUsed = !plan
    ? 0
    : plan.creditsUsed > plan.creditsPerPeriod
    ? plan.creditsPerPeriod
    : plan.creditsUsed;

  if (!plan) {
    return null;
  }

  return (
    <>
      {/* The reason for 239 is the width of the sidebar is 240px/15rem, but the "border" is a 1px box-shadow */}
      <Nav.Separator style={{ marginLeft: '-1rem', width: pxToRem(239) }} />
      <Nav.Group>
        <AccountInfo>
          <Stack space={2}>
            <div>
              <Stack space={2} orientation="horizontal" align="center">
                <IconPlan color="buttonIconPositive" />
                <Text font="regular14" color="white100" text={t(`plan${plan.planType}`)} />
              </Stack>
              {plan.dateToClose && (
                <Text
                  font="regular14"
                  color="textHelper"
                  text={t(daysUntilExpiration === 1 ? 'dayLeft' : 'daysLeft', {
                    days: daysUntilExpiration,
                  })}
                />
              )}
            </div>
            <Stack space={1}>
              <Stack orientation="horizontal" justify="space-between">
                <Text font="semibold12" color="textSecondary" text={t('credits')} />
                <Text
                  font="semibold12"
                  color="textSecondary"
                  text={`${formatNumber(creditsUsed)} / ${formatNumber(plan.creditsPerPeriod)}`}
                />
              </Stack>
              <ProgressBar
                ratio={plan.creditsUsed / plan.creditsPerPeriod}
                variant={plan.creditsUsed >= plan.creditsPerPeriod ? 'error' : 'info'}
              />
            </Stack>
          </Stack>
        </AccountInfo>
      </Nav.Group>
    </>
  );
};

const locale = {
  en: {
    collapse: 'Collapse',
    expand: 'Expand',
    journeys: 'Journeys',
    travellers: 'Traveller profiles',
    lists: 'Lists',
    settings: 'Settings',
    templates: 'Templates',
    aiAgents: 'AI Agents',
    portal: 'Portal',
    companyLogo: 'Company logo',
    plantrial: 'Premium • Free trial',
    planbusiness: 'Business',
    planpremium: 'Premium',
    planenterprise: 'Enterprise',
    plandemo_internal: 'Demo internal',
    plandemo_external: 'Demo external',
    daysLeft: '{days} days left',
    dayLeft: '{days} day left',
    credits: 'Credits',
    portalManagement: 'Portal management',
  },
};

const KeyboardShortcutText = styled.span({
  font: theme.font.regular14,
  color: theme.color.textHelper,
  whiteSpace: 'nowrap',
});

const AccountInfo = styled.div({
  marginTop: pxToRem(12),
  marginBottom: pxToRem(16),
});
